import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Card, Container, ListGroup, Spinner } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import api from '../services/api';
import { fetchPublicConferenceManuscripts } from '../services/api';

const PublicConferenceManuscripts = () => {
  const { conferenceCode } = useParams();
  const normalizedConferenceCode = useMemo(() => (conferenceCode || '').toUpperCase().trim(), [conferenceCode]);
  const [conference, setConference] = useState(null);
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadManuscripts = async () => {
      if (!normalizedConferenceCode) {
        setError('Missing conference code.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const response = await fetchPublicConferenceManuscripts(normalizedConferenceCode);
        setConference(response.conference || null);
        setPapers(Array.isArray(response.papers) ? response.papers : []);
      } catch (fetchError) {
        console.error('Error loading public manuscripts:', fetchError);
        setConference(null);
        setPapers([]);

        const status = fetchError?.response?.status;
        if (status === 404) {
          setError('Conference manuscripts were not found for the requested year.');
        } else if (status === 400) {
          setError('Invalid conference code in the URL.');
        } else {
          setError('Failed to load manuscripts. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    loadManuscripts();
  }, [normalizedConferenceCode]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Date unavailable';
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'Etc/GMT'
    });
  };

  const handleDownloadPaper = async (paper) => {
    try {
      const response = await api.get(paper.downloadUrl, {
        responseType: 'blob'
      });

      const contentDisposition = response.headers['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch?.[1] || `${paper.paperId}.pdf`;

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (downloadError) {
      console.error('Error downloading manuscript:', downloadError);
      setError('Failed to download the manuscript. Please try again.');
    }
  };

  return (
    <div className="public-manuscripts-page py-5">
      <Container>
        {/* <Card className="public-manuscripts-hero mb-4">
          <Card.Body className="p-4 p-md-5 text-white">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-end gap-3">
              <div>
                <p className="text-uppercase mb-2 public-manuscripts-kicker">Conference manuscripts</p>
                <h1 className="display-6 fw-bold mb-2">{normalizedConferenceCode || 'MFC00'} Manuscripts</h1>
                <p className="mb-0 public-manuscripts-subtitle">
                  Accepted papers with at least one confirmed registration are available here.
                </p>
              </div>
              <div className="text-md-end">
                <Badge bg="light" text="dark" className="px-3 py-2 rounded-pill">
                  Public downloads enabled
                </Badge>
              </div>
            </div>
          </Card.Body>
        </Card> */}



        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" variant="primary" />
            <p className="mt-3 mb-0 text-muted">Loading manuscripts...</p>
          </div>
        )}

        {!loading && error && (
          <Alert variant="warning" className="shadow-sm">
            {error}
            <div className="mt-3">
              <Link to="/" className="btn btn-outline-primary btn-sm">Return home</Link>
            </div>
          </Alert>
        )}

        {!loading && !error && conference && (
          <>
            <div className="row mb-3">
                <div className="col-12 text-center">
                    <div className="hero-section py-3" style={{
                        background: 'linear-gradient(135deg, #ff6b35 0%, #4a90e2 50%, #8e44ad 100%)',
                        borderRadius: '15px',
                        color: 'white',
                        padding: '12px 16px'
                    }}>
                        <h1 className="h3 fw-bold mb-2">{conference.title} · {formatDate(conference.startDate)} - {formatDate(conference.endDate)}</h1>
                        <p className="mb-0">Papers Accepted</p>

                    </div>
                </div>
            </div>
            {/* <div className="mb-4 d-flex flex-wrap gap-2 align-items-center">
                <h3>
                    <span className="text-muted">
                        Papers Accepted for the {conference.title} · {formatDate(conference.startDate)} - {formatDate(conference.endDate)}
                    </span>
                </h3>
            </div> */}

            <Card className="public-manuscripts-card">
              <Card.Header className="bg-white border-0 border-bottom px-4 pt-4 pb-0">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <h2 className="h5 mb-0">Available papers</h2>
                  <small className="text-muted">Click any paper to download the manuscript.</small>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                {papers.length > 0 ? (
                  <ListGroup variant="flush">
                    {papers.map((paper) => (
                      <ListGroup.Item
                        key={paper.id}
                        action
                        role="button"
                        tabIndex={0}
                        onClick={() => handleDownloadPaper(paper)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleDownloadPaper(paper);
                          }
                        }}
                        className="public-manuscripts-item px-4 py-3"
                      >
                        <div className="d-flex justify-content-between align-items-start gap-3">
                          <div>
                            <div className="public-manuscripts-id text-muted small mb-1">{paper.paperId}</div>
                            <div className="fw-semibold public-manuscripts-title">{paper.manuscriptTitle}</div>
                          </div>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <div className="p-4">
                    <Alert variant="info" className="mb-0">
                      No accepted manuscripts with registrations were found for this conference.
                    </Alert>
                  </div>
                )}
              </Card.Body>
            </Card>
          </>
        )}

        {!loading && !error && !conference && (
          <Alert variant="info">
            Manuscripts are unavailable for this conference.
          </Alert>
        )}
      </Container>
    </div>
  );
};

export default PublicConferenceManuscripts;